import axios from "axios";
import config from "../../config.json";

const AUTH_STORAGE_KEY = "persist:demo4-auth";
const DEFAULT_LIMIT = 10;

export function getPersistedAuthToken() {
  const raw = localStorage.getItem(AUTH_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);
    const token = parsed?.authToken;
    if (!token) {
      return null;
    }
    return token.replace(/['"]+/g, "");
  } catch (error) {
    console.error("Unable to parse auth token:", error);
    return null;
  }
}

export function clearPersistedAuthToken() {
  localStorage.removeItem(AUTH_STORAGE_KEY);
}

export function mapSearchResultsToRows(results = {}) {
  const { accounts = [], tickets = [] } = results;
  const rows = [];

  // Add tickets section first (usually more specific searches)
  if (tickets.length) {
    rows.push({ type: 0, text: "Tickets" });
    rows.push(
      ...tickets.map((ticket) => ({
        type: 1,
        icon: "ticket",
        text: `#${ticket.pk} - ${ticket.title || "Untitled"}`,
        description: buildTicketDescription(ticket),
        ticket,
      }))
    );
  }

  // Add accounts section
  if (accounts.length) {
    rows.push({ type: 0, text: "Accounts" });
    rows.push(
      ...accounts.map((account) => ({
        type: 1,
        icon: "account",
        text: account.name || "Account",
        description: buildAccountDescription(account),
        account,
      }))
    );
  }

  return rows;
}

// Keep the old function for backwards compatibility
export function mapAccountsToSearchRows(accounts = []) {
  if (!accounts.length) {
    return [];
  }

  const rows = [{ type: 0, text: "Accounts" }];
  rows.push(
    ...accounts.map((account) => ({
      type: 1,
      icon: "account",
      text: account.name || "Account",
      description: buildAccountDescription(account),
      account,
    }))
  );
  return rows;
}

function buildAccountDescription(account) {
  if (!account) {
    return "";
  }

  const segments = [];
  if (account.accountnumber) {
    segments.push(`Account #${account.accountnumber}`);
  }
  if (account.status) {
    segments.push(account.status);
  }
  if (account.phone) {
    segments.push(account.phone);
  }
  return segments.join(" · ");
}

function buildTicketDescription(ticket) {
  if (!ticket) {
    return "";
  }

  const segments = [];
  if (ticket.status) {
    segments.push(ticket.status);
  }
  if (ticket.priority) {
    segments.push(ticket.priority);
  }
  if (ticket.account_name) {
    segments.push(ticket.account_name);
  }
  return segments.join(" · ");
}

export async function searchGlobal(searchTerm, limit = DEFAULT_LIMIT) {
  if (!searchTerm?.trim()) {
    return { accounts: [], tickets: [] };
  }

  const authToken = getPersistedAuthToken();
  if (!authToken) {
    return { accounts: [], tickets: [] };
  }

  const response = await axios.get(`${config.urlBase}/global-search`, {
    params: { search: searchTerm, limit },
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  const result = response?.data?.result || {};
  // Handle both new format (object with accounts/tickets) and legacy format (array of accounts)
  if (Array.isArray(result)) {
    return { accounts: result, tickets: [] };
  }
  return {
    accounts: result.accounts || [],
    tickets: result.tickets || [],
  };
}

// Keep old function for backwards compatibility
export async function searchGlobalAccounts(searchTerm, limit = DEFAULT_LIMIT) {
  const result = await searchGlobal(searchTerm, limit);
  return result.accounts;
}
